/**
 * Shared schema.org JSON-LD payloads for the marketing surface.
 *
 * One source of truth so the homepage, /contact, footer-linked pages and
 * every product page advertise the same Organization entity. Keeping the
 * Organization stable across pages is what lets Google build the "knowledge
 * panel" sitelink and what AI engines use to disambiguate "FloGPT" the
 * brand from a chat-widget category.
 *
 * Update SOCIAL_PROFILES when a profile page is verified. Each URL here
 * returned a real company or product profile (not a login wall shared with
 * missing handles). Instagram and Facebook are linked in the footer but did
 * not return a profile page. youtube.com/@flogpt is a channel named FloGPT
 * with no link to flogpt.com. x.com/FloGPT is an unrelated account named Flo.
 */

import { localizePublicAssetUrl, marketingOrigin } from "./siteEntity";

/**
 * Per-call origin. The India build resolves to https://flogpt.in; the default
 * build keeps window.location.origin, which prerender rewrites to flogpt.com.
 */
const origin = (): string => marketingOrigin();

const SOCIAL_PROFILES = [
    "https://www.linkedin.com/company/110911552",
    "https://www.linkedin.com/company/flogpt",
    "https://www.linkedin.com/company/flogpt-ai",
];

/**
 * `dateModified` for long-lived schemas that are not one URL's git date
 * (homepage, legal WebPage, comparisons). Per-URL dates live in
 * `pageDates.ts` and are what the two product pages and the sitemap use.
 * Bump this only when the shared pages change visible copy, a fact, a
 * price, or a source. It is not a request clock and it is not printed
 * in the footer.
 */
export const SITE_LAST_UPDATED_ISO = "2026-10-16";
export const SITE_FIRST_PUBLISHED_ISO = "2024-01-15";

/**
 * Stable @id we use everywhere the Organization is referenced. Schema.org
 * recommends a fragment URL so all pages link to the same entity.
 */
export const ORG_ID = `${origin()}/#organization`;
export const WEBSITE_ID = `${origin()}/#website`;
export const HOMEPAGE_WEBPAGE_ID = `${origin()}/#webpage`;

export const buildOrganizationSchema = () => {
    // India is a separate return so the VDigit literals sit in a branch
    // production minification removes from the flogpt.com bundle.
    if (import.meta.env.VITE_SITE_REGION === "in") {
        const site = "https://flogpt.in";
        return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${site}/#organization`,
            name: "FloGPT India",
            legalName: "VDigit Solutions Private Limited",
            alternateName: ["floGPT", "FloGPT", "Flogpt"],
            url: `${site}/`,
            parentOrganization: {
                "@type": "Organization",
                "@id": "https://flogpt.com/#organization",
                name: "Pulse Real LLC",
                legalName: "Pulse Real LLC",
                url: "https://flogpt.com/",
            },
            address: {
                "@type": "PostalAddress",
                streetAddress: "Unit No 4, Office A, 4th Floor, EK Tower",
                addressLocality: "New Town, Kolkata",
                addressRegion: "West Bengal",
                postalCode: "700161",
                addressCountry: "IN",
            },
            telephone: "+91-9560303603",
            logo: {
                "@type": "ImageObject",
                url: `${site}/images/flogpt-iso.png`,
                width: 512,
                height: 512,
            },
            image: `${site}/images/og-default.jpg`,
            description:
                "FloGPT India is an AI Chat & Voice Agents platform with a built-in AI CRM, operated by VDigit Solutions Private Limited, a subsidiary of Pulse Real LLC.",
            knowsAbout: [
                "AI chat widgets",
                "AI voice agents",
                "Conversational AI",
                "Customer support automation",
                "Retrieval-augmented generation",
                "Omnichannel CRM",
                "Outbound AI calling",
                "Shopify automation",
            ],
            sameAs: SOCIAL_PROFILES,
            contactPoint: [
                {
                    "@type": "ContactPoint",
                    contactType: "customer support",
                    telephone: "+91-9560303603",
                    url: `${site}/contact`,
                    availableLanguage: ["English", "Hindi"],
                },
                {
                    "@type": "ContactPoint",
                    contactType: "sales",
                    telephone: "+91-9560303603",
                    url: `${site}/contact`,
                    availableLanguage: ["English", "Hindi"],
                },
            ],
        };
    }

    return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${origin()}/#organization`,
    name: "FloGPT",
    legalName: "Pulse Real LLC",
    alternateName: ["floGPT", "Flogpt"],
    url: `${origin()}/`,
    parentOrganization: {
        "@type": "Organization",
        name: "Pulse Real LLC",
        legalName: "Pulse Real LLC",
    },
    address: {
        "@type": "PostalAddress",
        addressLocality: "Sheridan",
        addressRegion: "WY",
        addressCountry: "US",
    },
    logo: {
        "@type": "ImageObject",
        url: `${origin()}/images/flogpt-iso.png`,
        width: 512,
        height: 512,
    },
    image: `${origin()}/images/og-default.jpg`,
    description:
        "FloGPT is an AI Chat & Voice Agents platform with a built-in AI CRM. Embed an AI chat widget on any website, run AI voice agents that answer & place phone calls 24/7, ground every conversation in your own knowledge base, and route chat, voice, email, SMS, WhatsApp, Instagram and Facebook into one unified ticket inbox. FloGPT is a product of Pulse Real LLC.",
    knowsAbout: [
        "AI chat widgets",
        "AI voice agents",
        "Conversational AI",
        "Customer support automation",
        "Retrieval-augmented generation",
        "Omnichannel CRM",
        "Outbound AI calling",
        "Shopify automation",
    ],
    sameAs: SOCIAL_PROFILES,
    contactPoint: [
        {
            "@type": "ContactPoint",
            contactType: "customer support",
            url: `${origin()}/contact`,
            availableLanguage: ["English", "Hindi"],
        },
        {
            "@type": "ContactPoint",
            contactType: "sales",
            url: `${origin()}/contact`,
            availableLanguage: ["English", "Hindi"],
        },
    ],
    };
};

export const buildWebSiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${origin()}/#website`,
    name: "FloGPT",
    alternateName: "FloGPT — AI Chat & Voice Agents",
    description:
        "FloGPT is an AI Chat & Voice Agents platform with a built-in AI CRM — chat widget, voice runtime, knowledge base, workflow builder, outbound triggers and unified omnichannel inbox in one product.",
    url: `${origin()}/`,
    publisher: { "@id": `${origin()}/#organization` },
    potentialAction: {
        "@type": "SearchAction",
        // Wires up Google's sitelinks search box. The search currently lives
        // inside `/blogs?search=…`; if we ever ship a global search we should
        // point this at it instead.
        target: {
            "@type": "EntryPoint",
            urlTemplate: `${origin()}/blogs?search={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
    },
    inLanguage: "en",
    dateModified: SITE_LAST_UPDATED_ISO,
});

export const buildSoftwareApplicationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "FloGPT",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Conversational AI / Customer Support",
    operatingSystem: "Web",
    url: `${origin()}/`,
    image: `${origin()}/images/og-default.jpg`,
    description:
        "AI Chat & Voice Agents platform with a built-in AI CRM. Embed AI chat on any website and run AI phone agents 24/7 — grounded in your own knowledge base. Includes a unified omnichannel inbox (chat, voice, email, SMS, WhatsApp, Instagram, Facebook), visual workflow builder, call pipeline editor, outbound triggers, and integrations for Shopify, calendars, and external CRMs.",
    publisher: { "@id": ORG_ID },
    sameAs: SOCIAL_PROFILES,
    mainEntityOfPage: { "@id": HOMEPAGE_WEBPAGE_ID },
    offers: {
        "@type": "Offer",
        name: "Free",
        description: "Free plan: $0, 100 messages/month. Paid plans from $49/month.",
        price: "0",
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `${origin()}/pricing`,
    },
    aggregateRating: undefined, // Add once we collect real reviews.
    datePublished: SITE_FIRST_PUBLISHED_ISO,
    dateModified: SITE_LAST_UPDATED_ISO,
    featureList: [
        "AI Chat Widget for any website",
        "AI Voice Agents (inbound + outbound)",
        "Unified AI CRM — chat, voice, email, SMS, WhatsApp, Instagram, Facebook",
        "Contact identity resolution across channels",
        "AI-to-human ticket handover with full transcript",
        "Status, priority and SLA tracking per ticket",
        "Role-scoped analytics (Self, Agent, Supervisor dashboards)",
        "Visual Call Pipeline editor",
        "Outbound Triggers (webhooks, time-relative, Shopify events)",
        "Knowledge Base ingestion (web crawl, PDFs, docs)",
        "Visual Workflow Builder",
        "16 languages including Hindi and 9 Indic locales",
        "Shopify integration",
        "Live human handover with full context",
    ],
});

/**
 * Build a `Service` schema for one of the /ai-* product pages. Combined with
 * the homepage Organization above, this tells Google + AI engines exactly
 * what each product page describes. Service > Product because these are
 * SaaS capabilities, not physical SKUs.
 *
 * https://schema.org/Service
 */
export const buildServiceSchema = (opts: {
    name: string;
    description: string;
    path: string;
    serviceType: string;
    /** Optional concrete features rendered as `hasOfferCatalog`. */
    features?: string[];
    /** Image override — defaults to og-default. */
    image?: string;
    /**
     * Authoritative works the page cites. Same names and URLs as the visible
     * Sources list. Omitted when the page has none.
     */
    citations?: { name: string; url: string }[];
    /**
     * Page-level `dateModified`. Omitted when unset so other Service pages
     * keep their current schema. Pass the same ISO as the visible Updated line.
     */
    dateModified?: string;
}) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    serviceType: opts.serviceType,
    url: `${origin()}${opts.path}`,
    image: opts.image
        ? opts.image.startsWith("http")
            ? localizePublicAssetUrl(opts.image)
            : `${origin()}${opts.image}`
        : `${origin()}/images/og-default.jpg`,
    provider: { "@id": ORG_ID },
    areaServed: ["Worldwide"],
    offers: {
        "@type": "Offer",
        url: `${origin()}/pricing`,
        priceSpecification: {
            "@type": "PriceSpecification",
            price: "0",
            priceCurrency: "USD",
        },
    },
    ...(opts.features && opts.features.length > 0
        ? {
              hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: `${opts.name} features`,
                  itemListElement: opts.features.map((feature) => ({
                      "@type": "Offer",
                      itemOffered: {
                          "@type": "Service",
                          name: feature,
                      },
                  })),
              },
          }
        : {}),
    ...(opts.citations && opts.citations.length > 0
        ? {
              citation: opts.citations.map((source) => ({
                  "@type": "CreativeWork",
                  name: source.name,
                  url: source.url,
              })),
          }
        : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
});

/**
 * Build a `CollectionPage` schema for index pages like /use-cases, /blogs.
 * https://schema.org/CollectionPage
 */
/**
 * Generic `WebPage` for long-lived routes (legal, docs hub) that are not a
 * Service or a blog post. `publisher` points at the stable Organization @id.
 */
export const buildWebPageSchema = (opts: {
    name: string;
    description: string;
    path: string;
    /** Page git date. Omitted callers keep the shared long-lived stamp. */
    dateModified?: string;
}) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url: `${origin()}${opts.path}`,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    inLanguage: "en",
    dateModified: opts.dateModified ?? SITE_LAST_UPDATED_ISO,
});

export const buildCollectionPageSchema = (opts: {
    name: string;
    description: string;
    path: string;
    itemCount?: number;
}) => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url: `${origin()}${opts.path}`,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORG_ID },
    ...(typeof opts.itemCount === "number"
        ? {
              mainEntity: {
                  "@type": "ItemList",
                  numberOfItems: opts.itemCount,
              },
          }
        : {}),
});

/**
 * `WebPage` schema for the homepage. The point of this block is twofold:
 *   1. Give Google + AI engines an explicit `mainEntity` ↔ Organization
 *      link so the homepage is recognised as the canonical page describing
 *      the FloGPT entity (this is what unlocks the knowledge-panel
 *      treatment in Google and clean entity attribution in AI overviews).
 *   2. Mark the answer-first definitional block (`#what-is-flogpt`) as
 *      `speakable`, which signals to voice assistants and AI engines that
 *      that prose is safe to read aloud verbatim. Selectors must hit DOM
 *      that's present without JS interaction — we keep the prose static
 *      and not collapsed.
 *
 * https://schema.org/WebPage and https://schema.org/SpeakableSpecification
 */
export const buildHomePageWebPageSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${origin()}/#webpage`,
    url: `${origin()}/`,
    name: "FloGPT — AI Chat & Voice Agents Platform with built-in AI CRM",
    description:
        "FloGPT is an AI Chat & Voice Agents platform with a built-in AI CRM. Embed an AI chat widget on any website, run AI phone agents 24/7, and unify chat, voice, email, SMS, WhatsApp, Instagram and Facebook into one inbox — grounded in your own knowledge base.",
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    primaryImageOfPage: {
        "@type": "ImageObject",
        url: `${origin()}/images/og-default.jpg`,
        width: 1200,
        height: 630,
    },
    speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["#what-is-flogpt", "#flogpt-tldr", "#flogpt-quickstart"],
    },
    inLanguage: "en",
    datePublished: SITE_FIRST_PUBLISHED_ISO,
    dateModified: SITE_LAST_UPDATED_ISO,
    publisher: { "@id": ORG_ID },
    license: `${origin()}/terms`,
});

/**
 * `HowTo` schema for the "deploy a FloGPT agent in three steps"
 * quick-start. AI engines surface HowTo blocks as numbered step lists in
 * answers to "how do I deploy / set up / install" queries — exactly the
 * top-of-funnel question for FloGPT.
 *
 * Keep step text < 200 chars each so AI engines can quote them verbatim
 * without truncation.
 *
 * https://schema.org/HowTo
 */
export const buildHomeQuickStartHowToSchema = () => ({
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to deploy a FloGPT AI chat and voice agent on your business",
    description:
        "Three steps to deploy a FloGPT AI agent: connect your knowledge base, customise a vertical template, then embed the chat widget script tag and attach a phone number for voice. Most teams ship a working agent end-to-end in under an hour.",
    totalTime: "PT60M",
    estimatedCost: {
        "@type": "MonetaryAmount",
        currency: "USD",
        value: "0",
    },
    image: `${origin()}/images/og-default.jpg`,
    supply: [
        { "@type": "HowToSupply", name: "Your website URL or sitemap" },
        { "@type": "HowToSupply", name: "Existing PDFs, docs, or FAQ pages (optional)" },
        { "@type": "HowToSupply", name: "A phone number to attach (for voice agents only)" },
    ],
    tool: [
        { "@type": "HowToTool", name: "FloGPT dashboard" },
        { "@type": "HowToTool", name: "Your website CMS or theme editor" },
    ],
    step: [
        {
            "@type": "HowToStep",
            position: 1,
            name: "Connect your knowledge base",
            url: `${origin()}/ai-knowledge-base`,
            text: `Sign up at ${import.meta.env.VITE_SITE_REGION === "in" ? "flogpt.in" : "flogpt.com"} and point the crawler at your website, sitemap, or upload PDFs and FAQ docs. FloGPT chunks, embeds and indexes the content automatically. Schedule recurring re-crawls so answers stay current.`,
        },
        {
            "@type": "HowToStep",
            position: 2,
            name: "Pick a vertical template and customise",
            url: `${origin()}/use-cases`,
            text: "Choose a vertical pack (Auto Dealers, Dental, Education, Fitness, Home Services, Hospitality, Insurance, Legal, Logistics, Real Estate, Restaurants, Retail, or General Support) which pre-configures the workflow, system prompt, voice scripts and recommended outbound triggers.",
        },
        {
            "@type": "HowToStep",
            position: 3,
            name: "Embed the chat widget and attach a phone number",
            url: `${origin()}/ai-chat-widget`,
            text: "Paste the FloGPT widget script tag before </body> on any website to launch chat. For voice, buy a phone number in-app or bring your own, then attach it to a Call Pipeline. Inbound calls and chat are answered immediately; tickets land in the unified CRM inbox.",
        },
    ],
});

/**
 * `BreadcrumbList` for the homepage. A single-item breadcrumb is
 * technically valid (Google accepts 1+ items) and explicitly anchors
 * "Home" to the FloGPT origin, which AI engines use as a fallback
 * citation label when they reference the page.
 */
export const buildHomeBreadcrumbSchema = () => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: `${origin()}/`,
        },
    ],
});

/**
 * Comparison ItemList — describes the FloGPT homepage's
 * "FloGPT vs. typical chat-only platforms" table as structured data so
 * AI engines (Perplexity, ChatGPT) can extract the comparison rows
 * directly when answering "FloGPT vs <competitor>" queries. We avoid
 * naming competitors in the schema body to stay factual; the visible
 * table is phrased as capability-vs-typical-stack.
 */
export const buildHomeComparisonItemListSchema = () => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "FloGPT capabilities vs. a typical chat-only AI platform",
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "AI chat widget for any website" },
        { "@type": "ListItem", position: 2, name: "AI voice agent on inbound + outbound phone calls" },
        { "@type": "ListItem", position: 3, name: "Unified CRM inbox across 7 channels" },
        { "@type": "ListItem", position: 4, name: "Cross-channel contact identity resolution" },
        { "@type": "ListItem", position: 5, name: "Knowledge-base-grounded responses with citations" },
        { "@type": "ListItem", position: 6, name: "Visual workflow + call pipeline editors" },
        { "@type": "ListItem", position: 7, name: "Outbound trigger engine (webhooks, Shopify, time-relative, scheduled lists)" },
        { "@type": "ListItem", position: 8, name: "16 voice languages including 9 Indic locales" },
    ],
});

/**
 * `WebPage` schema for a "FloGPT vs <competitor>" comparison page. We model it
 * as a WebPage whose `mainEntity` is an ItemList of the capabilities compared,
 * and explicitly `mentions` both the FloGPT Organization and the competitor as
 * an Organization. This gives AI engines a clean, factual structure to extract
 * when answering "FloGPT vs X" / "best X alternative" queries.
 *
 * `about` points at the FloGPT entity (the page is, ultimately, about FloGPT);
 * the competitor is referenced via `mentions` so we don't claim to describe it.
 */
export const buildComparisonWebPageSchema = (opts: {
    competitor: string;
    competitorDomain: string;
    path: string;
    name: string;
    description: string;
    capabilities: string[];
}) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${origin()}${opts.path}#webpage`,
    url: `${origin()}${opts.path}`,
    name: opts.name,
    description: opts.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    inLanguage: "en",
    dateModified: SITE_LAST_UPDATED_ISO,
    publisher: { "@id": ORG_ID },
    mentions: [
        { "@id": ORG_ID },
        {
            "@type": "Organization",
            name: opts.competitor,
            url: `https://${opts.competitorDomain}`,
        },
    ],
    mainEntity: {
        "@type": "ItemList",
        name: `${opts.name} — capabilities compared`,
        numberOfItems: opts.capabilities.length,
        itemListElement: opts.capabilities.map((capability, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: capability,
        })),
    },
});
